// Système de storage pour la progression Roulette à Rythmes
// Utilise Firestore avec fallback localStorage pour compatibilité

import RouletteProgressService from '../services/RouletteProgressService';
import { getStorageKey } from './userStorage';

const BASE_KEY = 'roulette_progress';

/**
 * Structure de progression pour un palier :
 * {
 *   completed: [
 *     { seed: "1-1-2-1", time: 12.5, errors: 2, score: 16.5, timestamp: 1234567890 },
 *     ...
 *   ]
 * }
 */

/**
 * Récupère la progression complète de la Roulette
 * @param {string|null} userId - ID de l'utilisateur (null = localStorage uniquement)
 * @returns {Promise<object>} Progression par palier
 */
export async function getRouletteProgress(userId = null) {
  try {
    if (userId) {
      // Récupérer depuis Firestore
      const firestoreProgress = await RouletteProgressService.getProgress(userId);

      // Migration automatique depuis localStorage si premier chargement Firestore
      const localProgress = getLocalStorageProgress();
      if (localProgress && hasLocalData(localProgress) && !hasFirestoreData(firestoreProgress)) {
        await RouletteProgressService.migrateFromLocalStorage(userId, localProgress);
        return await RouletteProgressService.getProgress(userId);
      }

      return firestoreProgress;
    } else {
      // Fallback localStorage
      return getLocalStorageProgress();
    }
  } catch (error) {
    console.error('Erreur lors du chargement de la progression Roulette:', error);
    return initializeProgress();
  }
}

/**
 * Récupère la progression depuis localStorage (legacy)
 * @returns {object} Progression par palier
 */
function getLocalStorageProgress() {
  try {
    const stored = localStorage.getItem(getStorageKey(BASE_KEY));
    if (!stored) {
      return initializeProgress();
    }
    return JSON.parse(stored);
  } catch (error) {
    console.error('Erreur lecture localStorage:', error);
    return initializeProgress();
  }
}

/**
 * Vérifie si la progression localStorage contient des données
 * @param {object} progress - Progression à vérifier
 * @returns {boolean}
 */
function hasLocalData(progress) {
  for (let i = 1; i <= 90; i++) {
    if (progress[i] && progress[i].completed && progress[i].completed.length > 0) {
      return true;
    }
  }
  return false;
}

/**
 * Vérifie si la progression Firestore contient des données
 * @param {object} progress - Progression à vérifier
 * @returns {boolean}
 */
function hasFirestoreData(progress) {
  for (let i = 1; i <= 90; i++) {
    if (progress[i] && progress[i].completed && progress[i].completed.length > 0) {
      return true;
    }
  }
  return false;
}

/**
 * Initialise une progression vierge pour les 90 paliers
 * @returns {object} Progression vide
 */
function initializeProgress() {
  const progress = {};
  for (let i = 1; i <= 90; i++) {
    progress[i] = {
      completed: []
    };
  }
  return progress;
}

/**
 * Sauvegarde la progression complète (localStorage uniquement - legacy)
 * @param {object} progress - Progression à sauvegarder
 */
export function saveRouletteProgress(progress) {
  try {
    localStorage.setItem(getStorageKey(BASE_KEY), JSON.stringify(progress));
  } catch (error) {
    console.error('Erreur lors de la sauvegarde de la progression Roulette:', error);
  }
}

/**
 * Enregistre un rythme complété pour un palier
 * Sauvegarde dans Firestore (si userId fourni) ET localStorage (fallback)
 * @param {number} tierId - ID du palier
 * @param {string} seed - Identifiant unique du rythme
 * @param {number} time - Temps en secondes
 * @param {number} errors - Nombre d'erreurs
 * @param {string|null} userId - ID de l'utilisateur (null = localStorage uniquement)
 * @param {number|null} averageTempo - Tempo moyen en BPM (pour Métronome Challenge)
 * @returns {Promise<object>} Progression mise à jour
 */
export async function saveRouletteScore(tierId, seed, time, errors, userId = null, averageTempo = null) {
  try {
    if (userId) {
      // Sauvegarder dans Firestore
      const progress = await RouletteProgressService.saveScore(userId, tierId, seed, time, errors, averageTempo);
      return progress;
    } else {
      // Fallback localStorage (legacy)
      const progress = getLocalStorageProgress();

      if (!progress[tierId]) {
        progress[tierId] = { completed: [] };
      }

      const score = time + (errors * 2);
      const timestamp = Date.now();
      const existingIndex = progress[tierId].completed.findIndex(c => c.seed === seed);

      const entry = {
        seed,
        time,
        errors,
        score,
        timestamp,
        averageTempo  // NOUVEAU: tempo moyen pour Métronome Challenge
      };

      if (existingIndex >= 0) {
        const existingScore = progress[tierId].completed[existingIndex].score;
        if (score < existingScore) {
          progress[tierId].completed[existingIndex] = entry;
        }
      } else {
        progress[tierId].completed.push(entry);
      }

      saveRouletteProgress(progress);
      return progress;
    }
  } catch (error) {
    console.error('❌ Erreur sauvegarde score Roulette:', error);
    throw error;
  }
}

/**
 * Récupère la progression pour un palier spécifique
 * @param {number} tierId - ID du palier
 * @param {string|null} userId - ID de l'utilisateur
 * @returns {Promise<object>} Progression du palier
 */
export async function getTierProgress(tierId, userId = null) {
  try {
    if (userId) {
      return await RouletteProgressService.getTierProgress(userId, tierId);
    } else {
      const progress = getLocalStorageProgress();
      const tierData = progress[tierId] || { completed: [] };
      return {
        completedCount: tierData.completed.length,
        totalScore: tierData.completed.reduce((sum, c) => sum + c.score, 0),
        totalErrors: tierData.completed.reduce((sum, c) => sum + c.errors, 0),
        completed: tierData.completed
      };
    }
  } catch (error) {
    console.error('❌ Erreur récupération stats palier:', error);
    return {
      completedCount: 0,
      totalScore: 0,
      totalErrors: 0,
      completed: []
    };
  }
}

/**
 * Récupère le score total pour un palier (somme de tous les scores)
 * @param {number} tierId - ID du palier
 * @param {string|null} userId - ID de l'utilisateur
 * @returns {Promise<number>} Score total en secondes
 */
export async function getTierTotalScore(tierId, userId = null) {
  const tierProgress = await getTierProgress(tierId, userId);
  return tierProgress.totalScore || 0;
}

/**
 * Récupère le nombre total d'erreurs pour un palier
 * @param {number} tierId - ID du palier
 * @param {string|null} userId - ID de l'utilisateur
 * @returns {Promise<number>} Total des erreurs
 */
export async function getTierTotalErrors(tierId, userId = null) {
  const tierProgress = await getTierProgress(tierId, userId);
  return tierProgress.totalErrors || 0;
}

/**
 * Récupère le meilleur score pour un rythme spécifique
 * @param {string} seed - Identifiant du rythme
 * @param {string|null} userId - ID de l'utilisateur
 * @returns {Promise<object|null>} Meilleur score ou null
 */
export async function getBestScoreForSeed(seed, userId = null) {
  const progress = await getRouletteProgress(userId);
  const tierId = parseInt(seed.split('-')[0]);

  if (!progress[tierId]) return null;

  const entry = progress[tierId].completed.find(c => c.seed === seed);
  return entry || null;
}

// Clé localStorage pour les paliers débloqués via le parcours
const PARCOURS_UNLOCKED_TIERS_KEY = 'roulette_parcours_tiers';

/**
 * Retourne la liste des IDs de paliers débloqués via le parcours guidé.
 * @returns {number[]} Liste des tier IDs
 */
export function getParcoursUnlockedTiers() {
  try {
    const stored = localStorage.getItem(PARCOURS_UNLOCKED_TIERS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Marque un palier comme débloqué via le parcours guidé.
 * Idempotent : n'ajoute pas de doublon.
 * @param {number} tierId
 */
export function markTierParcoursUnlocked(tierId) {
  try {
    const current = getParcoursUnlockedTiers();
    if (!current.includes(tierId)) {
      current.push(tierId);
      localStorage.setItem(PARCOURS_UNLOCKED_TIERS_KEY, JSON.stringify(current));
      console.log(`[Roulette] 🔓 Palier ${tierId} débloqué via parcours. Liste complète:`, current);
    } else {
      console.log(`[Roulette] ✅ Palier ${tierId} déjà débloqué. Liste:`, current);
    }
  } catch (error) {
    console.error('Erreur markTierParcoursUnlocked:', error);
  }
}

/**
 * Récupère le leaderboard global pour un palier
 * @param {number} tierId - ID du palier
 * @param {string|null} userId - ID de l'utilisateur
 * @returns {Promise<Array>} Classement
 */
export async function getTierLeaderboard(tierId, userId = null) {
  const tierProgress = await getTierProgress(tierId, userId);

  return [{
    rank: 1,
    name: "Toi",
    totalScore: tierProgress.totalScore || 0,
    totalErrors: tierProgress.totalErrors || 0,
    completedCount: tierProgress.completedCount || 0,
    isCurrentUser: true
  }];
}

/**
 * Réinitialise la progression complète (localStorage uniquement - pour debug)
 */
export function resetRouletteProgress() {
  localStorage.removeItem(getStorageKey(BASE_KEY));
}

/**
 * Exporte la progression en JSON (pour backup)
 * @param {string|null} userId - ID de l'utilisateur
 * @returns {Promise<string>} JSON de la progression
 */
export async function exportRouletteProgress(userId = null) {
  const progress = await getRouletteProgress(userId);
  return JSON.stringify(progress, null, 2);
}

/**
 * Importe une progression depuis JSON (localStorage uniquement - pour restore)
 * @param {string} jsonData - JSON de la progression
 */
export function importRouletteProgress(jsonData) {
  try {
    const progress = JSON.parse(jsonData);
    saveRouletteProgress(progress);
  } catch (error) {
    console.error('❌ Erreur lors de l\'import:', error);
  }
}
